<template>
  <div class="table-responsive-lg invoice-details-grid">
    <table class="table table-striped blue items-with-sub-lines">
      <thead>
        <tr>
          <th>{{ getContentItem("orders") }}</th>
          <th />
          <th>{{ getContentItem("Line") }}</th>
          <th>{{ getContentItem("PartNumber") }}</th>
          <th>{{ getContentItem("description") }}</th>

          <template v-if="details.isNotRapidPartsInvoice">
            <th>{{ getContentItem("poNumber") }}</th>
            <th>{{ getContentItem("htcCode") }}</th>
          </template>

          <th>{{ getContentItem("container") }}</th>

          <th>{{ getContentItem("shipped") }}</th>

          <th v-if="isSapDealerWithSoldTo">
            {{ getContentItem("dealer") }}
          </th>
          <th>
            {{ getContentItem("trackingNumber") }}
          </th>
          <th v-if="showLineItemMasterTrackingInfo">
            {{ getContentItem("masterTrackingNumber") }}
          </th>

          <template v-if="details.showPrices">
            <th v-if="details.SubTotalEmergencySurcharges">
              Emergency Fee
            </th>
            <th>{{ getContentItem("price") }}</th>
            <th>{{ getContentItem("ExtCoreCosts") }}</th>
            <th>{{ getContentItem("ExtPrice") }}</th>
          </template>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in lineItems">
          <tr :key="index">
            <td 
              v-if="item.rowSpan" 
              :rowspan="item.rowSpan + GetRowSpanForComments()"
            >
              <related-order-link v-if="GetRelatedOrder(item.SalesOrderNumber) !== undefined" :item="GetRelatedOrder(item.SalesOrderNumber)" />
              <span v-else>
                {{ item.SalesOrderNumber }}
              </span>
            </td>
            <template v-if="item.ItemNumber === 'M'">
              <td />
              <td class="right muted">
                {{ item.SalesOrderLineNumber }}
              </td>
              <td class="muted">
                <em>Comment</em>
              </td>
              <td colspan="100%" class="muted">
                {{ item.ItemDescription }}
              </td>
            </template>
            <template v-else>
              <td>
                <input type="checkbox" :value="item" v-model="selectedLineItems" />
              </td>
              <td>{{ item.SalesOrderLineNumber }}</td>
              <td>
                <part-details-link :oem-part-code="item.ItemNumber" />
              </td>
              <td>{{ item.ItemDescription }}</td>

              <template v-if="details.isNotRapidPartsInvoice">
                <td>{{ item.PurchaseOrderNumber }}</td>                
                <td>{{ item.HarmonizedTarriffCode }}</td>
              </template>

              <td>{{ item.ContainerNumber }}</td>

              <td>{{ item.QuantityShipped }}</td>

              <td v-if="isSapDealerWithSoldTo">
                {{ item.SoldTo }}
              </td>
              <td>
                <template v-if="item.CarrierTrackingUrl">
                  <a :href="item.CarrierTrackingUrl">{{ item.CarrierTrackingNumber }}</a>
                </template>
                <template v-else>
                  {{ item.CarrierTrackingNumber }}
                </template>
              </td>
              <td v-if="showLineItemMasterTrackingInfo">
                <template v-if="item.MasterCarrierTrackingUrl">
                  <a :href="item.MasterCarrierTrackingUrl">{{ item.MasterCarrierTrackingNumber }}</a>
                </template>
                <template v-else>
                  {{ item.MasterCarrierTrackingNumber }}
                </template>
              </td>

              <template v-if="details.showPrices">
                <td v-if="details.SubTotalEmergencySurcharges">
                  <currency-text :amount="item.EmergencySurcharge" :currency-code="details.CurrencyCode" />
                </td>
                <td>
                  <currency-text :amount="item.UnitPrice" :currency-code="details.CurrencyCode" />
                </td>
                <td>
                  <currency-text :amount="item.CoreCharge" :currency-code="details.CurrencyCode" />
                </td>
                <td>
                  <currency-text
                    :amount="item.ExtendedPrice"
                    :currency-code="details.CurrencyCode"
                  />
                </td>
              </template>
            </template>
          </tr>
          <template v-for="(comment, commentIndex) in item.Comments">
            <tr :key="commentIndex" />
            <tr :key="commentIndex">
              <td />
              <td>
                {{ item.SalesOrderLineNumber }}
              </td>
              <td>
                {{ getContentItem("Comment") }}
              </td>
              <td :colspan="4">
                {{ comment }}
              </td>
              <td />
            </tr>
          </template>
        </template>
      </tbody>
      <tfoot v-if="details.showPrices">
        <tr v-if="showStartClaimButtonLabel" class="row-sub-total idg__table-row">
          <td colspan="6" class="idg__claim-button-label">
            <label>
              <strong>
                Cannot start claim, invoice is older than {{ this.claimAllowedDays }} days.
              </strong>
            </label>
          </td>
        </tr>
        <tr v-if="showIscalaReturnsDisabledLabel" class="row-sub-total idg__table-row">
          <td colspan="6" class="idg__claim-button-label">
            <label>
              <strong>
                {{ getContentItem("IscalaInvoice")}}
              </strong>
            </label>
          </td>
        </tr>
        <tr class="row-sub-total">
          <td>
            <DnFunctionButton
              id="createClaim"
              :callback-fn="createClaim"
              :text="getContentItem('startClaim')"
              :size="largeSize"
              extra-classes="idg__claim-button"
              :loading="isLoadingClaimData"
              :disabled="disableCreateClaimButton"
            />
          </td>
          
          <td colspan="100%">
            <label for="subtotal" class="inline-label">{{ getContentItem("subtotal") }}:</label>
            <currency-text
              id="subtotal"
              :amount="details.SubTotalParts"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalEmergencySurcharges" class="row-sub-fee">
          <td colspan="100%">
            <label
              for="emergency-surcharges"
              class="inline-label"
            >{{ getContentItem("emergencySurcharge") }}:</label>
            <currency-text
              id="emergency-surcharges"
              :amount="details.SubTotalEmergencySurcharges"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalCores" class="row-sub-fee">
          <td colspan="100%">
            <label for="subtotal-cores" class="inline-label">{{ getContentItem("coreCost") }}:</label>
            <currency-text
              id="subtotal-cores"
              :amount="details.SubTotalCores"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalDiscount" class="row-sub-fee">
          <td colspan="100%">
            <label for="restock-fee" class="inline-label">{{ getContentItem("restockFee") }}:</label>
            <currency-text
              id="restock-fee"
              :amount="details.SubTotalDiscount"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr class="row-sub-fee">
          <td colspan="100%">
            <label for="freight" class="inline-label">{{ getContentItem("freight") }}:</label>
            <currency-text
              id="freight"
              :amount="details.SubTotalFreight"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubtotalVat" class="row-sub-fee">
          <td colspan="100%">
            <label for="gst" class="inline-label">{{ getContentItem("gst") }}:</label>
            <currency-text
              id="gst"
              :amount="details.SubtotalVat"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.OtherFees" class="row-sub-fee">
          <td colspan="100%">
            <label for="other-fees" class="inline-label">{{ getContentItem("otherFees") }}:</label>
            <currency-text
              id="other-fees"
              :amount="details.OtherFees"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.DealerClaimAmount" class="row-sub-fee">
          <td colspan="100%">
            <label for="claimAmount" class="inline-label">{{ getContentItem("ClaimAmount") }}:</label>
            <currency-text
              id="claimAmount"
              :amount="details.DealerClaimAmount"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.DealerServiceFee" class="row-sub-fee">
          <td colspan="100%">
            <label for="dealerServiceFee" class="inline-label">{{ getContentItem("DealerServiceFee") }}:</label>
            (<currency-text
              id="dealerServiceFee"
              :amount="details.DealerServiceFee"
              :currency-code="details.CurrencyCode"
            />)
          </td>
        </tr>
        <tr v-if="details.TotalInvoice && details.showTotal" class="row-total">
          <td colspan="100%">
            <label for="total" class="inline-label">{{ getContentItem("total") }}:</label>
            <currency-text
              id="total"
              :amount="details.TotalInvoice"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalDiscount" class="row-discount">
          <td colspan="100%">
            <label for="discount" class="inline-label">{{ getContentItem("cashDiscountAvailable") }}:</label>
            <currency-text
              id="discount"
              :amount="details.SubTotalDiscount"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
  import { mapGetters } from "vuex";

  // components
  import { CurrencyText, PartDetailsLink } from "@/components/Elements";
  import RelatedOrderLink from "@/components/Invoices/RelatedOrderLink"
  import DnFunctionButton from "@/components/Elements/DnFunctionButton"
  
  // mixins
  import { sizeHelper } from "@/components/mixins/sizeHelper"

  // services 
  import { ClaimsService } from "@/services/ClaimsService"

  export default {
    name: "InvoiceDetailsGrid",
    components: {
      CurrencyText,
      PartDetailsLink,
      RelatedOrderLink,
      DnFunctionButton
    },
    mixins: [ sizeHelper ],
    data: () => ({
      selectedLineItems: [],
      isLoadingClaimData: false,
      claimAllowedDays: null
    }),
    created() {
          return ClaimsService.GetClaimAllowedDays(this.McfCostCenterId).then(days => {
            this.claimAllowedDays = days;
          });
    },
    computed: {
      ...mapGetters([
        "getContentItem", 
        "user", 
        "activeAccountId",
        "McfCostCenterId",
      ]),

      lineItems() {
        const list = this.details.LineItems || [];

        return list.map((item, i) => {
          return {
            ...item,
            rowSpan:
              i === 0 || item.SalesOrderNumber !== list[i - 1].SalesOrderNumber
                ? list.filter(x => x.SalesOrderNumber === item.SalesOrderNumber)
                  .length
                : null
          };
        });
      },

      isSuperReturnAdmin() { 
        return this.user.UserPermissions.includes("IgnoreReturnValidation");
      },

      isPastClaimAllowedDays() {

        const dayLength = 24 * 60 * 60 * 1000;
        const invoiceDate = new Date(this.details.InvoiceDate);
        const today = new Date();
        const difference = Math.round(Math.abs((today - invoiceDate) / dayLength));

        return difference > this.claimAllowedDays;
      },

      isSapDealerWithSoldTo() {
        return (
          this.details.InvoiceSource !== "Rapidsparts" &&
          this.lineItems.some(x => x.SoldTo)
        );
      },

      disableCreateClaimButton() {
        const noPartSelected = this.selectedLineItems.length < 1;
        const pastMaxClaimDays = this.isPastClaimAllowedDays;
        const canReturnPastMaxDays = this.isSuperReturnAdmin;
        const isIscalaInvoice = this.details.SourceCode === "ISC";

        if (noPartSelected) {
          return true;
        }

        if (isIscalaInvoice) {
          return true;
        }
             
        if (canReturnPastMaxDays) {
          return false;
        } else if (pastMaxClaimDays) {
          return true;
        } else {
          return false;
        }
        // fallback to not allow
        return true;
      },

      showStartClaimButtonLabel() {
        return this.isPastClaimAllowedDays && !this.isSuperReturnAdmin;
      },

      showIscalaReturnsDisabledLabel(){
        return this.details.SourceCode === "ISC";
      },

      showLineItemTrackingInfo() {
        return this.lineItems.some(x => x.CarrierTrackingNumber);
      },

      showLineItemMasterTrackingInfo() {
        return this.lineItems.some(x => x.MasterCarrierTrackingNumber);
      }
    },

    methods: {
      GetRelatedOrder(orderNumber) {
        let self = this;
        return this.relatedOrders.find(function(element) {

          if(element.OrderNumber === orderNumber) {
            if(self.$route.query.invoiceSource == "Rapidparts") {
                element.OrderSource = "Rpi";
              }
          }

          return element.OrderNumber === orderNumber
        })
        return undefined
      },
      
      async getPastClaimAllowedDays() {
        //load claim allowed days
        var claimAllowedDays = await ClaimsService.GetClaimAllowedDays(this.McfCostCenterId);
        return claimAllowedDays;
      },
      
      createClaim(){
        this.isLoadingClaimData = true
        //Had an issue with 'this' going out of scope in the below arrow function
        let self = this;
        ClaimsService.LoadClaimInvoiceItems(self.activeAccountId, self.user.UserId, self.$route.query.invoiceId, self.$route.query.invoiceSource).then(allInvoiceItems => {
          let selectedLineIds = self.selectedLineItems.map(x => x.Id);
          let selectedInvoiceItems = allInvoiceItems.data.filter(x => selectedLineIds.includes(x.InvoiceLineId));

          let invoiceId = self.$route.query.invoiceId;
          let invoiceNumber = self.details.InvoiceNumber
          let invoiceSource = self.details.InvoiceSource
          let invoiceDate = self.details.InvoiceDate
          let salesOrg = self.details.SalesOrganization

          let relatedOrder = self.details.RelatedOrders[0]
          let orderNumber = relatedOrder.OrderNumber
          let dealerCode = relatedOrder.DealerCode

          let claimData = {
            CustomerReferenceNumber: `${dealerCode} - ${invoiceNumber}`,
            Source: String,
            InvoiceSource: invoiceSource,
            InvoiceDate: invoiceDate,
            InvoiceNumber: invoiceNumber,
            InvoiceFreight: null,
            DealerCode: dealerCode,
            OrderNumber: orderNumber,
            Items: selectedInvoiceItems,
            ContactName: String(),
            ContactPhone: String(),
            ContactPhoneExtension: String(),
            ContactEmail: String(),
            ContactFax: String(),
            SalesOrganization: salesOrg,
            ClaimType: "PartsIssue",
            FreightExpectedCredit: 0.00,
            FreightComment: "",
            ClaimImagesBase64: [],
            ClaimImagesFilenames: [],
            SourceInvoiceNumber: self.details.SourceInvoiceNumber,
            SourceCode: self.details.SourceCode
          }

          let invoiceData = {
            invoiceId: invoiceId,
            invoiceSource: invoiceSource
          }

          self.$store.commit("SET_RECENT_COMPONENT", {
            name: "InvoiceCreditDetails",
            data: {
              claimData: claimData,
              invoiceData: invoiceData,
              currentTabId: "claimInfoTab"
            },
          });

          this.isLoadingClaimData = false

          this.$router.push({
            name: "CreateClaim",
            query: { fromInvoice: true}
          })
        })
      },
      GetRowSpanForComments(){
        var count = 0;
        for(var i = 0; i < this.details.LineItems.length; i++){
          count += this.details.LineItems[i].Comments.length;
        }
        return count * 2;
      }
    },

    props: {
      details: Object,
      relatedOrders: Array
    }
  };
</script>

<style lang="scss">
  .idg__claim-button{
    width: 110px;
  }

  .idg__table-row {
    .idg__claim-button-label {
      text-align: left;
      color: red;
      padding: 8px 0 0 12px;
    }
  }
</style>
